import React, { useCallback, useEffect, useRef, useState } from 'react';
import LinkButton from './LinkButton';
import './BgProcesses.css';

// Background processes — the detached processes the editor spawns for long
// runs (an illustrations batch today; more kinds follow). One row per process:
// what it is, whose it is, its pid, its last heartbeat and status, with Stop
// (the process kills its agents and keeps finished results) and Kill (SIGTERM,
// then SIGKILL) for one that does not answer. A row unfolds into its params,
// the recorded error and the tail of its log file.
//
// Admin only (the API is). Refreshes itself every 5 s while open.

const REFRESH_MS = 5000;
const LIVE = ['starting', 'running', 'stopping'];

const fmtTime = (t) => (t ? new Date(t).toLocaleString() : '—');
const ago = (t) => {
  if (!t) return '—';
  const s = Math.max(0, Math.round((Date.now() - new Date(t).getTime()) / 1000));
  if (s < 90) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)} min ago`;
  return `${(s / 3600).toFixed(1)} h ago`;
};
const dur = (a, b) => {
  if (!a) return '—';
  const ms = (b ? new Date(b).getTime() : Date.now()) - new Date(a).getTime();
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

const STATUS_HINT = {
  starting: 'Row written, process spawned, not reported in yet',
  running: 'Alive and beating',
  stopping: 'Stop requested — the process is finishing what it can and leaving',
  done: 'Finished on its own',
  stopped: 'Stopped on request; finished parts kept',
  error: 'Ended with an error (see the row)',
  killed: 'Killed by signal',
  lost: 'Heartbeat gone and no such pid — the host rebooted or the process died without a word',
};

const Status = ({ row }) => {
  const label = (row.managed && row.status === 'starting' ? 'queued' : row.status) + (row.stale && LIVE.includes(row.status) ? ' · silent' : '');
  const title = STATUS_HINT[row.status] + (row.stale ? '. The heartbeat is late but the pid is still there.' : '');
  return <span className={`bgp-status bgp-status-${row.status}`} title={title}>{label}</span>;
};

const BgProcesses = ({ onClose, closeHref }) => {
  const [data, setData] = useState({ rows: [], liveCount: 0, maxParallel: null, host: '' });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [showFinished, setShowFinished] = useState(true);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/bg?limit=200', { credentials: 'include' });
      if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
      setData(await r.json());
      setError(null);
    } catch (e) {
      setError(`Could not load the process list: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (id) => {
    try {
      const r = await fetch(`/api/bg/${id}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
      setDetail(await r.json());
    } catch (e) {
      setDetail({ error: `Could not load the process: ${e.message}` });
    }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(() => {
      load();
      if (openId) loadDetail(openId);
    }, REFRESH_MS);
    return () => clearInterval(timer.current);
  }, [load, loadDetail, openId]);

  const toggle = (id) => {
    if (openId === id) { setOpenId(null); setDetail(null); return; }
    setOpenId(id);
    setDetail(null);
    loadDetail(id);
  };

  const act = async (id, action, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusyId(id);
    try {
      const r = await fetch(`/api/bg/${id}/${action}`, { method: 'POST', credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `${r.status}`);
      await load();
      if (openId === id) loadDetail(id);
    } catch (e) {
      setError(`${action} failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const selftest = async () => {
    setError(null);
    try {
      const r = await fetch('/api/bg/selftest', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ seconds: 30 }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `${r.status}`);
      await load();
    } catch (e) {
      setError(`Self-test did not start: ${e.message}`);
    }
  };

  const rows = showFinished ? data.rows : data.rows.filter((r) => LIVE.includes(r.status));

  return (
    <div className="bgp-page">
      <div className="bgp-header">
        <div>
          <h2>Background processes</h2>
          <p className="bgp-sub">
            Background runners and audio alignment: queued, running and completed attempts, with errors and logs.
            Detached runners survive an editor restart; alignment tasks are managed by the editor and retain their history here.
            <b>Stop</b> and <b>Kill</b> control detached runners.
            {data.maxParallel ? ` Detached runners: ${data.liveCount} of ${data.maxParallel} allowed` : ''}{data.host ? ` on ${data.host}` : ''}.
          </p>
        </div>
        <div className="bgp-header-actions">
          <label className="bgp-toggle"><input type="checkbox" checked={showFinished} onChange={(e) => setShowFinished(e.target.checked)} /> show finished</label>
          <button type="button" className="bgp-btn" onClick={load} title="Reload the list now (it reloads itself every 5 s)">Refresh</button>
          <button type="button" className="bgp-btn" onClick={selftest} title="Spawn a 30-second process that only ticks — to see spawn, heartbeat, Stop and Kill on this server">Run self-test</button>
          <LinkButton className="bgp-btn bgp-btn-close" href={closeHref} onClick={onClose}>Close</LinkButton>
        </div>
      </div>

      {error && <div className="bgp-error">{error}</div>}
      {loading && <div className="bgp-note">Loading…</div>}
      {!loading && !rows.length && <div className="bgp-note">No processes{showFinished ? ' recorded yet' : ' running right now'}.</div>}

      {rows.length > 0 && (
        <table className="bgp-table">
          <thead>
            <tr>
              <th>Kind</th>
              <th>Quest / run</th>
              <th>Started by</th>
              <th>Started</th>
              <th>Took</th>
              <th>Heartbeat</th>
              <th>Status</th>
              <th>PID</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const live = LIVE.includes(r.status);
              const open = openId === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr className={`bgp-row${live ? ' bgp-row-live' : ''}${open ? ' bgp-row-open' : ''}`} onClick={() => toggle(r.id)}>
                    <td><span className="bgp-kind">{r.kind}</span>{r.node && r.node !== r.kind ? <span className="bgp-dim"> · {r.node}</span> : null}</td>
                    <td>
                      {r.quest_id || <span className="bgp-dim">—</span>}
                      {r.params && r.params.versionId ? <div>Recording #{r.params.versionId}</div> : null}
                      {r.location_uuid ? <div className="bgp-dim">point {r.location_uuid.slice(0, 8)}</div> : null}
                      {r.run_id ? <div className="bgp-dim" title={r.run_id}>run {r.run_id.slice(0, 8)}</div> : null}
                    </td>
                    <td>{r.started_by || '—'}</td>
                    <td title={fmtTime(r.started_at)}>{ago(r.started_at)}</td>
                    <td>{dur(r.started_at, r.finished_at)}</td>
                    <td title={fmtTime(r.heartbeat_at)}>{live ? ago(r.heartbeat_at) : <span className="bgp-dim">—</span>}</td>
                    <td><Status row={r} />{r.summary && r.summary.progress && r.summary.progress.label ? <div className="bgp-dim">{r.summary.progress.label}</div> : null}{r.error ? <div className="bgp-warn" title={r.error}>{r.error.slice(0, 160)}</div> : null}{live && r.alive === false ? <div className="bgp-warn">no such pid</div> : null}</td>
                    <td className="bgp-mono">{r.pid || '—'}</td>
                    <td className="bgp-actions" onClick={(e) => e.stopPropagation()}>
                      {live && !r.managed && r.status !== 'stopping' && (
                        <button type="button" className="bgp-btn bgp-btn-small" disabled={busyId === r.id} onClick={() => act(r.id, 'stop')} title="Ask the process to stop: it kills its agents, keeps finished parts and writes its status">Stop</button>
                      )}
                      {live && !r.managed && (
                        <button type="button" className="bgp-btn bgp-btn-small bgp-btn-danger" disabled={busyId === r.id} onClick={() => act(r.id, 'kill', `Kill process ${r.pid || ''} (${r.kind})? SIGTERM now, SIGKILL after 5 s. Unfinished parts of the run are lost; the run goes "interrupted".`)} title="SIGTERM now, SIGKILL after 5 s">Kill</button>
                      )}
                    </td>
                  </tr>
                  {open && (
                    <tr className="bgp-detail-row">
                      <td colSpan={9}>
                        {!detail && <div className="bgp-note">Loading…</div>}
                        {detail && detail.error && !detail.id && <div className="bgp-error">{detail.error}</div>}
                        {detail && detail.id && (
                          <div className="bgp-detail">
                            <div className="bgp-detail-grid">
                              <div><span className="bgp-dim">id</span> <span className="bgp-mono">{detail.id}</span></div>
                              <div><span className="bgp-dim">host</span> {detail.host || '—'}</div>
                              <div><span className="bgp-dim">started</span> {fmtTime(detail.started_at)}</div>
                              <div><span className="bgp-dim">finished</span> {fmtTime(detail.finished_at)}</div>
                              <div><span className="bgp-dim">exit</span> {detail.exit_code == null && !detail.exit_signal ? '—' : `${detail.exit_code == null ? '' : `code ${detail.exit_code}`}${detail.exit_signal ? ` ${detail.exit_signal}` : ''}`}</div>
                              <div><span className="bgp-dim">stop requested</span> {detail.cancel_requested ? `${detail.cancel_requested_by || 'yes'} · ${fmtTime(detail.cancel_requested_at)}` : 'no'}</div>
                              <div><span className="bgp-dim">log file</span> <span className="bgp-mono">{detail.log_path || '—'}</span></div>
                            </div>
                            {detail.params && Object.keys(detail.params).length > 0 && (
                              <details className="bgp-fold"><summary>params</summary><pre>{JSON.stringify(detail.params, null, 2)}</pre></details>
                            )}
                            {detail.summary && (
                              <details className="bgp-fold" open><summary>summary</summary><pre>{JSON.stringify(detail.summary, null, 2)}</pre></details>
                            )}
                            {detail.error && (
                              <details className="bgp-fold" open><summary>error</summary><pre className="bgp-pre-error">{detail.error}</pre></details>
                            )}
                            <details className="bgp-fold" open={!LIVE.includes(detail.status)}>
                              <summary>log tail{LIVE.includes(detail.status) ? ' (live, refreshes with the list)' : ''}</summary>
                              <pre>{detail.logTail || '(empty)'}</pre>
                            </details>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default BgProcesses;
